import type { EpisodeListFilters } from "@/api/datasetEpisode";
import type { DatasetReviewMode } from "@/components/dataset/review/useDatasetReview";
import { notifyWindowCapReached, windowStack, windowTaskbarTextLabel } from "@/app/windowStack";
import {
  defaultDatasetDetailTab,
  type DatasetDetailTab,
} from "@/page/datasetDetail/datasetDetailTabs";

// datasetDetail kind 엔트리의 params 계약이다. 목록 페이지가 open 할 때 채우고, 콘텐츠
// (DatasetDetailWindowContent)가 읽는다. 한 곳에서만 정의해 drift 를 막는다.
export type DatasetDetailWindowParams = {
  datasetUuid: string;
  // 리뷰 탭의 동작이다.
  //   - direct: 편집 후 저장(PUT).
  //   - candidate: 후보 확정/폐기와 검토 완료(리뷰 큐 진입).
  mode: DatasetReviewMode;
  // 열 때 활성화할 탭이다. 열린 창을 다른 탭으로 다시 열면 이 값이 바뀌고, 콘텐츠가 그 탭으로 전환한다.
  initialTab: DatasetDetailTab;
  // 어노테이션 세트 딥링크가 지정한 초기 로드 세트. 어노테이션 탭이 소비한다.
  annotationSetUuid?: string;
  episodeFilters?: EpisodeListFilters;
};

export type OpenDatasetDetailWindowInput = {
  datasetUuid: string;
  displayName: string;
  mode?: DatasetReviewMode;
  initialTab?: DatasetDetailTab;
  annotationSetUuid?: string;
  episodeFilters?: EpisodeListFilters;
  // 창이 닫힐 때 호출한다(예: 목록 새로고침). archive/북마크/설정 변경을 목록에 반영한다.
  onClose?: () => void;
};

// 데이터셋 상세를 전체화면 창 스택으로 연다(예: 목록 행 클릭). 같은 데이터셋 창이 이미 열려
// 있으면 그 창을 복원한다. Cap(5)을 초과하면 창을 열지 않고 toast 로 안내한다.
//
// 열린 창을 다른 mode·탭으로 다시 열 수 있다(예: Dataset 목록에서 연 창을 리뷰 큐에서 다시
// 클릭). windowStack.open 은 기존 엔트리의 params 를 갱신하지 않는다. 그래서 여기서 update 로
// 덮어쓴다. 값이 같으면 update 가 no-op 이므로 불필요한 렌더가 생기지 않는다.
export function openDatasetDetailWindow(input: OpenDatasetDetailWindowInput): void {
  const params: DatasetDetailWindowParams = {
    datasetUuid: input.datasetUuid,
    mode: input.mode ?? "direct",
    initialTab: input.initialTab ?? defaultDatasetDetailTab,
    annotationSetUuid: input.annotationSetUuid,
    episodeFilters: input.episodeFilters,
  };
  const label = windowTaskbarTextLabel(
    params.mode === "candidate" ? "리뷰:" : "공용:",
    input.displayName,
  );
  const id = windowStack.open({
    kind: "datasetDetail",
    key: input.datasetUuid,
    title: input.displayName,
    label,
    params,
    onClose: input.onClose,
  });
  if (id === null) {
    notifyWindowCapReached();
    return;
  }
  windowStack.update(id, { params, label });
}

// 활성 창이 리뷰 큐에서 연(candidate 모드) 데이터셋 창이면 그 datasetUuid 를 반환한다. 아니면 null 이다.
// 리뷰 페이지가 ?review URL 미러링에 쓴다. 창은 app-level 이라 라우터에 접근할 수 없다.
// 그래서 페이지가 유일한 writer 다.
export function activeReviewDataset(): string | null {
  const { entries, activeId } = windowStack.getSnapshot();
  const active = entries.find((entry) => entry.id === activeId);
  if (active === undefined || active.kind !== "datasetDetail") {
    return null;
  }
  const params = active.params as DatasetDetailWindowParams;
  return params.mode === "candidate" ? params.datasetUuid : null;
}
